import React, { FunctionComponent } from 'react';
import { CLOSE_PATHS, CLOSE_VIEWBOX } from '../../../svg/paths/close';
import { STATUS_PATHS } from '../../../svg/paths/status';
import classnames from 'classnames';
import { RecommendModalProps } from './types';
import { PLAN_CONFIG, PlanTerm } from '../../../types/companyAccount';

type Props = RecommendModalProps & {
  minimumTerm: PlanTerm;
  basicTerm: PlanTerm;
  premierTerm: PlanTerm;
  setMinimumTerm: (term: PlanTerm) => void;
  setBasicTerm: (term: PlanTerm) => void;
  setPremierTerm: (term: PlanTerm) => void;
};

export const Presentation: FunctionComponent<Props> = ({
  isModalOpen,
  onClose,
  minimumTerm,
  basicTerm,
  premierTerm,
  setMinimumTerm,
  setBasicTerm,
  setPremierTerm,
}) => {
  const formatYen = (value: number) => `${value / 10000}万円`;

  return (
    <div className={classnames('plan-billing-change__modal-overlay', { 'plan-billing-change__modal-overlay--active': isModalOpen })} onClick={onClose}>
      <div className={classnames('plan-billing-change__modal', 'plan-billing-change__modal--xwide', {
        'plan-billing-change__modal--active': isModalOpen
      })} onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className="plan-billing-change__modal-header">
          <div className="plan-billing-change__modal-title">プランアップグレード</div>
          <button className="plan-billing-change__modal-close-button" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12">
              <path
                d="M0.735395 12L0 11.2646L5.26461 6L0 0.735394L0.735395 0L6 5.26461L11.2646 0L12 0.735394L6.7354 6L12 11.2646L11.2646 12L6 6.73539L0.735395 12Z"
                fill="#19190F"
              />
            </svg>
          </button>
        </div>

        {/* メインコンテンツ */}
        <div className="plan-billing-change__modal-main">
          <div className="plan-billing-change__modal-contents">
            {/* 各プランの特徴 */}
            <div className="plan-billing-change__modal-section">
              <p className="plan-billing-change__modal-section-title">機能を利用するにはプランをアップグレードする必要があります</p>
              <p className="plan-billing-change__modal-section-description">
                プランをアップグレードすることで、選択したプランに応じた機能が利用可能になります。<br />
                詳細はプランページからもご確認いただけます。
              </p>
            </div>

            {/* プラン比較表 */}
            <div className="plan-billing-change__modal-section">
              <p className="plan-billing-change__modal-section-title">プラン比較表</p>
              <div className="plan-billing-change__modal-table-wrapper">
                <div className="plan-billing-change__modal-table">
                  {/* ヘッダー行 */}
                  <div className="plan-billing-change__modal-table-row plan-billing-change__modal-table-row--xwide plan-billing-change__modal-table-header">
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide plan-billing-change__modal-table-cell-header">
                      <p className="plan-billing-change__modal-table-header-text">プレミア</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide plan-billing-change__modal-table-cell-header">
                      <p className="plan-billing-change__modal-table-header-text">ベーシック</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide plan-billing-change__modal-table-cell-header">
                      <p className="plan-billing-change__modal-table-header-text">ミニマム</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide plan-billing-change__modal-table-cell-header">
                      <p className="plan-billing-change__modal-table-header-text">ショート</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide plan-billing-change__modal-table-cell-header">
                      <p className="plan-billing-change__modal-table-header-text">成功報酬</p>
                    </div>
                  </div>

                  {/* 概要 */}
                  <div className="plan-billing-change__modal-table-row plan-billing-change__modal-table-row--xwide">
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <p className="plan-billing-change__modal-table-value-text plan-billing-change__modal-table-value-text--long">中長期で、しっかり採用人数を確保したい企業様におすすめ</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <p className="plan-billing-change__modal-table-value-text plan-billing-change__modal-table-value-text--long">上位掲載・スカウトが使えて、最も選ばれているプラン</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <p className="plan-billing-change__modal-table-value-text plan-billing-change__modal-table-value-text--long">急ぎの採用でなく、良い人がいればという企業様におすすめ</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <p className="plan-billing-change__modal-table-value-text plan-billing-change__modal-table-value-text--long">予算を抑えて、短期間で採用したい企業様向け</p>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <p className="plan-billing-change__modal-table-value-text plan-billing-change__modal-table-value-text--long">採用人数が少なくじっくり採用を検討したい方におすすめ</p>
                    </div>
                  </div>

                  {/* 機能の使用可否 */}
                  <div className="plan-billing-change__modal-table-row plan-billing-change__modal-table-row--xwide">
                  <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-feature-content">
                        <p className="plan-billing-change__modal-table-feature-title">主な機能</p>
                        <div className="plan-billing-change__modal-table-feature-list">
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">気になる</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.stop} fill="#FF3B30" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">スカウト</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">上位掲載</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-feature-content">
                        <p className="plan-billing-change__modal-table-feature-title">主な機能</p>
                        <div className="plan-billing-change__modal-table-feature-list">
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">気になる</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">スカウト</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">上位掲載</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-feature-content">
                        <p className="plan-billing-change__modal-table-feature-title">主な機能</p>
                        <div className="plan-billing-change__modal-table-feature-list">
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">気になる</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.stop} fill="#FF3B30" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">スカウト</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.stop} fill="#FF3B30" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">上位掲載</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-feature-content">
                        <p className="plan-billing-change__modal-table-feature-title">主な機能</p>
                        <div className="plan-billing-change__modal-table-feature-list">
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">気になる</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">スカウト</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">上位掲載</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-feature-content">
                        <p className="plan-billing-change__modal-table-feature-title">主な機能</p>
                        <div className="plan-billing-change__modal-table-feature-list">
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">気になる</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">スカウト</p>
                          </div>
                          <div className="plan-billing-change__modal-table-feature-item">
                            <div className="plan-billing-change__modal-table-feature-icon">
                              <svg className="plan-billing-change__modal-svg" fill="none" preserveAspectRatio="none" viewBox="0 0 12 12">
                                <g>
                                  <path d={STATUS_PATHS.check} fill="#007800" />
                                </g>
                              </svg>
                            </div>
                            <p className="plan-billing-change__modal-table-feature-text">上位掲載</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 価格 */}
                  <div className="plan-billing-change__modal-table-row plan-billing-change__modal-table-row--xwide">
                    {/* Premier Plan */}
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-pricing-content">
                        <div className="plan-billing-change__modal-table-pricing-info">
                          <div className="plan-billing-change__modal-table-pricing-tags">
                            <div
                              className={classnames(
                                'plan-billing-change__modal-table-pricing-tag',
                                premierTerm === 'half'
                                  ? 'plan-billing-change__modal-table-pricing-tag--dark'
                                  : 'plan-billing-change__modal-table-pricing-tag--light'
                              )}
                              onClick={() => setPremierTerm('half')}
                            >
                              半年
                            </div>
                            <div
                              className={classnames(
                                'plan-billing-change__modal-table-pricing-tag',
                                premierTerm === 'year'
                                  ? 'plan-billing-change__modal-table-pricing-tag--dark'
                                  : 'plan-billing-change__modal-table-pricing-tag--light'
                              )}
                              onClick={() => setPremierTerm('year')}
                            >
                              年間
                            </div>
                          </div>
                          <div className="plan-billing-change__modal-table-pricing-price">
                            <div className="plan-billing-change__modal-table-pricing-price-inner">
                              <span className="plan-billing-change__modal-table-pricing-price-amount">
                                {formatYen(PLAN_CONFIG.premier.prices[premierTerm])}
                              </span>
                              <span className="plan-billing-change__modal-table-pricing-price-unit">/月</span>
                            </div>
                          </div>
                        </div>
                        <a
                          href={`/accounts/plan_change?plan_id=${PLAN_CONFIG.premier.planIds[premierTerm]}`}
                          className="plan-billing-change__modal-table-pricing-button"
                        >
                          {PLAN_CONFIG.premier.label}
                        </a>
                      </div>
                    </div>

                    {/* Basic Plan */}
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-pricing-content">
                        <div className="plan-billing-change__modal-table-pricing-info">
                          <div className="plan-billing-change__modal-table-pricing-tags">
                            <div
                              className={classnames(
                                'plan-billing-change__modal-table-pricing-tag',
                                basicTerm === 'half'
                                  ? 'plan-billing-change__modal-table-pricing-tag--dark'
                                  : 'plan-billing-change__modal-table-pricing-tag--light'
                              )}
                              onClick={() => setBasicTerm('half')}
                            >
                              半年
                            </div>
                            <div
                              className={classnames(
                                'plan-billing-change__modal-table-pricing-tag',
                                basicTerm === 'year'
                                  ? 'plan-billing-change__modal-table-pricing-tag--dark'
                                  : 'plan-billing-change__modal-table-pricing-tag--light'
                              )}
                              onClick={() => setBasicTerm('year')}
                            >
                              年間
                            </div>
                          </div>
                          <div className="plan-billing-change__modal-table-pricing-price">
                            <div className="plan-billing-change__modal-table-pricing-price-inner">
                              <span className="plan-billing-change__modal-table-pricing-price-amount">
                                {formatYen(PLAN_CONFIG.basic.prices[basicTerm])}
                              </span>
                              <span className="plan-billing-change__modal-table-pricing-price-unit">/月</span>
                            </div>
                          </div>
                        </div>
                        <a
                          href={`/accounts/plan_change?plan_id=${PLAN_CONFIG.basic.planIds[basicTerm]}`}
                          className="plan-billing-change__modal-table-pricing-button"
                        >
                          {PLAN_CONFIG.basic.label}
                        </a>
                      </div>
                    </div>

                    {/* Minimum Plan */}
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-pricing-content">
                        <div className="plan-billing-change__modal-table-pricing-info">
                          <div className="plan-billing-change__modal-table-pricing-tags">
                            <div
                              className={classnames(
                                'plan-billing-change__modal-table-pricing-tag',
                                minimumTerm === 'half'
                                  ? 'plan-billing-change__modal-table-pricing-tag--dark'
                                  : 'plan-billing-change__modal-table-pricing-tag--light'
                              )}
                              onClick={() => setMinimumTerm('half')}
                            >
                              半年
                            </div>
                            <div
                              className={classnames(
                                'plan-billing-change__modal-table-pricing-tag',
                                minimumTerm === 'year'
                                  ? 'plan-billing-change__modal-table-pricing-tag--dark'
                                  : 'plan-billing-change__modal-table-pricing-tag--light'
                              )}
                              onClick={() => setMinimumTerm('year')}
                            >
                              年間
                            </div>
                          </div>
                          <div className="plan-billing-change__modal-table-pricing-price">
                            <div className="plan-billing-change__modal-table-pricing-price-inner">
                              <span className="plan-billing-change__modal-table-pricing-price-amount">
                                {formatYen(PLAN_CONFIG.minimum.prices[minimumTerm])}
                              </span>
                              <span className="plan-billing-change__modal-table-pricing-price-unit">/月</span>
                            </div>
                          </div>
                        </div>
                        <a
                          href={`/accounts/plan_change?plan_id=${PLAN_CONFIG.minimum.planIds[minimumTerm]}`}
                          className="plan-billing-change__modal-table-pricing-button"
                        >
                          {PLAN_CONFIG.minimum.label}
                        </a>
                      </div>
                    </div>

                    {/* Short Plan */}
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-pricing-content">
                        <div className="plan-billing-change__modal-table-pricing-info">
                          <div className="plan-billing-change__modal-table-pricing-tags">
                            <div className="plan-billing-change__modal-table-pricing-tag plan-billing-change__modal-table-pricing-tag--dark">
                              単月利用のみ
                            </div>
                          </div>
                          <div className="plan-billing-change__modal-table-pricing-price">
                            <div className="plan-billing-change__modal-table-pricing-price-inner">
                              <span className="plan-billing-change__modal-table-pricing-price-amount">15万円</span>
                              <span className="plan-billing-change__modal-table-pricing-price-unit">/月</span>
                            </div>
                          </div>
                        </div>
                        <a
                          href={`/accounts/plan_change?plan_id=${PLAN_CONFIG.short.planId}`}
                          className="plan-billing-change__modal-table-pricing-button"
                        >
                          {PLAN_CONFIG.short.label}
                        </a>
                      </div>
                    </div>

                    {/* Success Fee Plan */}
                    <div className="plan-billing-change__modal-table-cell plan-billing-change__modal-table-cell--xwide">
                      <div className="plan-billing-change__modal-table-pricing-content">
                        <div className="plan-billing-change__modal-table-pricing-info">
                          <div className="plan-billing-change__modal-table-pricing-tags">
                            <div className="plan-billing-change__modal-table-pricing-tag plan-billing-change__modal-table-pricing-tag--dark">都度請求</div>
                          </div>
                          <div className="plan-billing-change__modal-table-pricing-fee-details">
                            <div className="plan-billing-change__modal-table-pricing-fee-item">
                              <p className="plan-billing-change__modal-table-pricing-fee-label">新卒採用</p>
                              <p className="plan-billing-change__modal-table-pricing-fee-amount">50万円</p>
                            </div>
                            <div className="plan-billing-change__modal-table-pricing-fee-item">
                              <p className="plan-billing-change__modal-table-pricing-fee-label">新卒以外の採用</p>
                              <p className="plan-billing-change__modal-table-pricing-fee-amount">
                                <span className="plan-billing-change__modal-table-pricing-fee-amount-prefix">理論年収の</span>
                                <span className="plan-billing-change__modal-table-pricing-fee-amount-value">15%</span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <a
                          href={`/accounts/plan_change?plan_id=${PLAN_CONFIG.success.planId}`}
                          className="plan-billing-change__modal-table-pricing-button"
                        >
                          {PLAN_CONFIG.success.label}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
